import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layouts/ClientLayout';
import { CheckCircle, X, MessageSquare, Eye, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const ClientApprovalsPage = () => {
  const { api } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchApprovals();
  }, []);

  const fetchApprovals = async () => {
    try {
      const response = await api.get('/approvals/pending');
      setApprovals(response.data);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (decision) => {
    if (!selectedApproval) return;
    setSubmitting(true);
    try {
      await api.patch(`/approvals/${selectedApproval.id}`, {
        decision,
        comments: comment,
      });
      setDetailOpen(false);
      setComment('');
      fetchApprovals();
    } catch (error) {
      console.error('Failed to submit decision:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-approvals">
        <div>
          <h1 className="text-2xl font-syne font-bold text-white">Approvals</h1>
          <p className="text-fp-textMuted mt-1">{approvals.length} items pending your review</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : approvals.length === 0 ? (
          <div className="text-center py-12 bg-fp-surface border border-white/5 rounded-sm">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">All caught up!</h3>
            <p className="text-fp-textMuted">No pending approvals at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {approvals.map((approval) => (
              <motion.div
                key={approval.id}
                className="bg-fp-surface border border-white/5 rounded-sm overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`approval-card-${approval.id}`}
              >
                {/* Preview */}
                <div className="h-40 bg-fp-bg flex items-center justify-center">
                  <Eye className="w-8 h-8 text-fp-textMuted" />
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                      Pending Review
                    </span>
                    {approval.deliverable && (
                      <span className="px-2 py-1 bg-white/5 text-fp-textMuted text-xs rounded">
                        {approval.deliverable.type}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-white mb-2">
                    {approval.deliverable?.title || 'Deliverable'}
                  </h3>
                  <p className="text-fp-textMuted text-sm mb-4">
                    Due: {approval.deliverable?.due_date || 'Not set'}
                  </p>
                  <Button
                    onClick={() => {
                      setSelectedApproval(approval);
                      setDetailOpen(true);
                    }}
                    className="w-full btn-primary py-2"
                    data-testid={`review-btn-${approval.id}`}
                  >
                    Review & Decide
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Approval Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="bg-fp-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-syne">Review Deliverable</DialogTitle>
            </DialogHeader>
            {selectedApproval?.deliverable && (
              <div className="space-y-6">
                {/* Preview */}
                <div className="aspect-video bg-fp-bg rounded-sm flex items-center justify-center">
                  <Eye className="w-12 h-12 text-fp-textMuted" />
                </div>

                {/* Info */}
                <div>
                  <h2 className="text-xl font-semibold text-white mb-2">
                    {selectedApproval.deliverable.title}
                  </h2>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded">
                      {selectedApproval.deliverable.type}
                    </span>
                    {selectedApproval.deliverable.platforms?.map((platform) => (
                      <span
                        key={platform}
                        className="px-2 py-1 bg-white/5 text-fp-textMuted text-xs rounded capitalize"
                      >
                        {platform.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Caption */}
                {selectedApproval.deliverable.caption && (
                  <div>
                    <h3 className="text-fp-textMuted text-sm mb-2">Caption</h3>
                    <p className="p-4 bg-fp-bg rounded-sm text-white">
                      {selectedApproval.deliverable.caption}
                    </p>
                  </div>
                )}

                {/* Comment */}
                <div>
                  <h3 className="text-fp-textMuted text-sm mb-2">Add Comment (optional)</h3>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Any feedback or specific changes needed..."
                    className="bg-fp-bg border-white/10 text-white"
                    rows={3}
                    data-testid="approval-comment"
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => handleDecision('approved')}
                    disabled={submitting}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    data-testid="approve-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleDecision('changes')}
                    disabled={submitting}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="changes-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 mr-2" />
                    )}
                    Request Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default ClientApprovalsPage;
